import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { monitoringQueries } from '../../lib/queries';
import type { AlertFilters } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';

/* ─── Types ──────────────────────────────────────────────────────── */
type Severity = 'critical' | 'high' | 'medium' | 'low';
type AlertStatus = 'open' | 'acknowledged' | 'resolved';
type Tab = 'alerts' | 'rules' | 'silences' | 'vendors' | 'channels';

interface AlertItem {
  id: string;
  rule_id: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  alert_type: string;
  message: string;
  severity: string;
  old_value: string | null;
  new_value: string | null;
  details: string | null;
  is_read: number;
  is_acknowledged: number;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  is_resolved: number | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_note: string | null;
  created_at: string;
}

interface Rule {
  id: string;
  name: string;
  type: string;
  vendor_id: string | null;
  threshold_value: number | null;
  threshold_percentage: number | null;
  consecutive_count: number | null;
  cooldown_minutes: number | null;
  severity: string;
  is_active: number;
  last_triggered: string | null;
  alerts_30d: number;
}

interface Silence {
  id: string;
  vendor_id: string | null;
  rule_id: string | null;
  reason: string | null;
  created_by: string | null;
  starts_at: string | null;
  ends_at: string;
  created_at: string;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  config: string;
  is_active: number;
}

interface VendorRow {
  id: string;
  name: string;
  domain: string;
  score: number | null;
  risk_level: string | null;
  last_scan: string | null;
  monitoring_paused: boolean;
  score_threshold: number;
  risk_level_drift: number;
  open_alerts: number;
  critical_open: number;
  last_alert_at: string | null;
}

interface Stats {
  open_alerts: number;
  open_by_severity: Record<string, number>;
  unacknowledged_alerts: number;
  active_rules: number;
  active_silences: number;
  daily_trend: { date: string; count: number }[];
  vendors_watched: number;
  vendors_paused: number;
  enabled_channels: number;
}

/* ─── Catalogue: what each trigger really does (mirrors services/monitoring_service.py) ─── */
type SeverityMode = 'rule' | 'computed';
interface Trigger {
  label: string;
  desc: string;
  icon: string;
  severityMode: SeverityMode;
  severityNote?: string;
  available: boolean;
  unavailableNote?: string;
}

const TRIGGERS: Record<string, Trigger> = {
  score_drop: {
    label: 'Score dropped', icon: 'trendDown', severityMode: 'rule', available: true,
    desc: "A vendor's security score falls by at least a set number of points between two scans.",
  },
  score_threshold: {
    label: 'Score below floor', icon: 'floor', severityMode: 'computed', available: true,
    severityNote: 'Critical below 40, otherwise High.',
    desc: "A vendor's score is under its floor (set per vendor on the Vendors tab, default 70).",
  },
  risk_change: {
    label: 'Risk level increased', icon: 'stairs', severityMode: 'computed', available: true,
    severityNote: 'Critical for a jump of 2+ levels, otherwise High.',
    desc: 'A vendor moves up the Low → Medium → High → Critical scale by the per-vendor step (default 2).',
  },
  new_critical: {
    label: 'New critical finding', icon: 'flame', severityMode: 'computed', available: true,
    severityNote: 'Always Critical.',
    desc: "A Critical finding shows up that wasn't in the vendor's previous scan.",
  },
  indicator_regression: {
    label: 'Check regressed', icon: 'undo', severityMode: 'rule', available: true,
    desc: 'A High or Critical check that passed on the previous scan now fails.',
  },
  cert_expiry: {
    label: 'Certificate expiring', icon: 'lock', severityMode: 'computed', available: true,
    severityNote: 'Critical within 7 days, High within 14, Medium within 30.',
    desc: "The vendor's TLS certificate expires within 30 days (checked daily).",
  },
  compliance_drop: {
    label: 'Compliance score dropped', icon: 'shield', severityMode: 'rule', available: false,
    unavailableNote: "Scans don't compute a compliance score yet, so this rule can't fire.",
    desc: "The vendor's compliance score falls by the per-vendor percentage.",
  },
};
const triggerOf = (type: string): Trigger =>
  TRIGGERS[type] || { label: type.replace(/_/g, ' '), icon: 'bell', severityMode: 'computed', available: true, desc: '' };
const ALERT_TYPE_LABEL = (type: string) => (type === 'escalation' ? 'Escalation' : type === 'test' ? 'Test' : triggerOf(type).label);

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];
const SEV: Record<Severity, { label: string; fg: string; bg: string; bd: string; dfg: string; dbg: string }> = {
  critical: { label: 'Critical', fg: '#B91C1C', bg: '#FEF2F2', bd: '#FECACA', dfg: '#F87171', dbg: '#2a0f0f' },
  high: { label: 'High', fg: '#C2410C', bg: '#FFF7ED', bd: '#FED7AA', dfg: '#FB923C', dbg: '#2a160a' },
  medium: { label: 'Medium', fg: '#A16207', bg: '#FEFCE8', bd: '#FDE68A', dfg: '#FACC15', dbg: '#241f08' },
  low: { label: 'Low', fg: '#1D4ED8', bg: '#EFF6FF', bd: '#BFDBFE', dfg: '#60A5FA', dbg: '#0b1a33' },
};
const sevKey = (s: string): Severity => (SEVERITIES.includes((s || '').toLowerCase() as Severity) ? (s || '').toLowerCase() as Severity : 'medium');

const STATUS: Record<AlertStatus, { label: string; fg: string; bg: string; dfg: string; dbg: string }> = {
  open: { label: 'Open', fg: '#B91C1C', bg: '#FEF2F2', dfg: '#F87171', dbg: '#2a0f0f' },
  acknowledged: { label: 'Acknowledged', fg: '#A16207', bg: '#FEFCE8', dfg: '#FACC15', dbg: '#241f08' },
  resolved: { label: 'Resolved', fg: '#15803D', bg: '#F0FDF4', dfg: '#4ADE80', dbg: '#0b2214' },
};
const alertStatus = (a: AlertItem): AlertStatus => (a.is_resolved ? 'resolved' : a.is_acknowledged ? 'acknowledged' : 'open');

const COOLDOWNS = [
  { value: 0, label: 'Every time' },
  { value: 60, label: 'At most hourly' },
  { value: 360, label: 'At most every 6 hours' },
  { value: 1440, label: 'At most daily' },
  { value: 10080, label: 'At most weekly' },
];
const cooldownLabel = (m: number | null) =>
  COOLDOWNS.find(c => c.value === (m || 0))?.label || `At most every ${m} min`;

const SILENCE_DURATIONS = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: '1 day', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '1 week', hours: 168 },
];

const APP_HEADER_HEIGHT = 64;
const PANEL_MS = 200;
const PAGE_SIZE = 25;
const INVALIDATE = [['monitoring'], ['notifications']];

/* ─── Helpers ────────────────────────────────────────────────────── */
const apiError = (e: any, fallback: string) => e?.response?.data?.detail
  ? (typeof e.response.data.detail === 'string' ? e.response.data.detail : fallback) : fallback;

const parseUtc = (iso: string) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
const toDate = (iso: string) => {
  // Backend timestamps are naive local server time; treat them as local unless they carry an offset.
  const local = new Date(iso);
  return isNaN(local.getTime()) ? parseUtc(iso) : local;
};
const ago = (iso: string | null) => {
  if (!iso) return 'Never';
  const s = Math.max(0, (Date.now() - toDate(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return toDate(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const until = (iso: string) => {
  const s = (toDate(iso).getTime() - Date.now()) / 1000;
  if (s <= 0) return 'ending';
  if (s < 3600) return `${Math.ceil(s / 60)}m left`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m left`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h left`;
};
const fmtDateTime = (iso: string | null) => (iso
  ? toDate(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—');
const dayLabel = (iso: string) => {
  const d = toDate(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - that.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const parseJson = (raw: string | null): any => { try { return raw ? JSON.parse(raw) : {}; } catch { return {}; } };
const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
const isHttps = (s: string) => /^https:\/\/[^\s]+$/i.test(s.trim());

/* ─── Icons ──────────────────────────────────────────────────────── */
const I: Record<string, string> = {
  bell: 'M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.9 1.9 0 0 0 3.4 0',
  plus: 'M12 5v14M5 12h14',
  check: 'M5 12l5 5L20 7',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  mute: 'M11 5 6 9H2v6h4l5 4V5ZM23 9l-6 6M17 9l6 6',
  trendDown: 'M23 18l-9.5-9.5-5 5L1 6M17 18h6v-6',
  floor: 'M3 20h18M7 16V8M12 16V4M17 16v-5',
  stairs: 'M3 20h5v-5h5v-5h5V5h3',
  flame: 'M12 22c4 0 7-3 7-7 0-5-5-7-5-12-3 2-4 5-4 7-1-1-2-2-2-4-2 2-3 5-3 9 0 4 3 7 7 7Z',
  undo: 'M3 7v6h6M3 13a9 9 0 1 0 3-7.7L3 8',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 6l-10 7L2 6',
  hash: 'M4 9h16M4 15h16M10 3 8 21M16 3l-2 18',
  teams: 'M17 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3 7h10v10H3zM8 10v6M6 10h4M15 11h6v5a3 3 0 0 1-6 0',
  webhook: 'M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2M6 17l3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06M12 6l3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z',
  external: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  pause: 'M6 4h4v16H6zM14 4h4v16h-4z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  warn: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  chevron: 'M9 18l6-6-6-6',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8',
};
const Icon = ({ d, size = 14, style, sw = 2 }: { d: string; size?: number; style?: React.CSSProperties; sw?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }} aria-hidden="true"><path d={d} /></svg>
);

/* ─── Theme ──────────────────────────────────────────────────────── */
const palette = (isDark: boolean) => ({
  isDark,
  ink: isDark ? '#e2e8f0' : T.ink,
  ink2: isDark ? '#cbd5e1' : T.ink2,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#7c8aa0' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  line2: isDark ? '#171717' : '#EEF2F6',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  hover: isDark ? '#121212' : '#F8FAFC',
  bg: isDark ? '#000000' : T.bg,
  accent: isDark ? '#3B82F6' : '#2563EB',
  accentBg: isDark ? '#0f1e38' : '#EFF6FF',
  red: isDark ? '#F87171' : T.red,
  green: isDark ? '#4ADE80' : T.grn,
  amber: isDark ? '#FACC15' : T.amb,
});
type P = ReturnType<typeof palette>;

const btn = (c: P, kind: 'primary' | 'secondary' | 'ghost' | 'danger' = 'secondary', small = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: small ? '5px 10px' : '8px 14px', borderRadius: 7, fontSize: small ? 12 : 12.5, fontWeight: 600,
  fontFamily: sans, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.2,
  border: kind === 'secondary' ? `1px solid ${c.line}` : '1px solid transparent',
  background: kind === 'primary' ? c.accent : kind === 'danger' ? (c.isDark ? '#7f1d1d' : T.red) : kind === 'ghost' ? 'transparent' : c.surface,
  color: kind === 'primary' || kind === 'danger' ? '#fff' : kind === 'ghost' ? c.slate : c.ink,
});
const input = (c: P): React.CSSProperties => ({
  width: '100%', padding: '8px 10px', borderRadius: 7, fontSize: 13, fontFamily: sans, boxSizing: 'border-box',
  border: `1px solid ${c.line}`, background: c.surface, color: c.ink, outline: 'none',
});
const label = (c: P): React.CSSProperties => ({ fontSize: 12, fontWeight: 600, color: c.ink2, marginBottom: 6, display: 'block' });
const panel = (c: P): React.CSSProperties => ({ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10 });

/* ─── Small components ───────────────────────────────────────────── */
function SevChip({ sev, c }: { sev: string; c: P }) {
  const s = SEV[sevKey(sev)];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
      color: c.isDark ? s.dfg : s.fg, background: c.isDark ? s.dbg : s.bg, border: `1px solid ${c.isDark ? s.dfg + '40' : s.bd}`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: c.isDark ? s.dfg : s.fg }} />{s.label}
    </span>
  );
}

function StatusChip({ status, c }: { status: AlertStatus; c: P }) {
  const s = STATUS[status];
  return (
    <span style={{
      display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color: c.isDark ? s.dfg : s.fg, background: c.isDark ? s.dbg : s.bg,
    }}>{s.label}</span>
  );
}

function Chip({ children, c, tone = 'neutral', title }: { children: React.ReactNode; c: P; tone?: 'neutral' | 'blue' | 'amber' | 'green' | 'red'; title?: string }) {
  const tones = {
    neutral: [c.slate, c.surface2],
    blue: [c.isDark ? '#93C5FD' : '#1D4ED8', c.accentBg],
    amber: [c.isDark ? '#FACC15' : '#A16207', c.isDark ? '#241f08' : '#FEFCE8'],
    green: [c.isDark ? '#4ADE80' : '#15803D', c.isDark ? '#0b2214' : '#F0FDF4'],
    red: [c.isDark ? '#F87171' : '#B91C1C', c.isDark ? '#2a0f0f' : '#FEF2F2'],
  } as const;
  const [fg, bg] = tones[tone];
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 600,
      color: fg, background: bg, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Switch({ on, onChange, disabled, c, labelText }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; c: P; labelText: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={labelText} disabled={disabled}
      onClick={e => { e.stopPropagation(); onChange(!on); }}
      style={{
        width: 34, height: 20, borderRadius: 99, border: 'none', padding: 2, cursor: disabled ? 'not-allowed' : 'pointer',
        background: on ? c.accent : (c.isDark ? '#2a2a2a' : '#CBD5E1'), opacity: disabled ? 0.55 : 1,
        display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start', transition: 'background .15s', flexShrink: 0,
      }}>
      <span style={{ width: 16, height: 16, borderRadius: 99, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)' }} />
    </button>
  );
}

function Segmented<V extends string>({ value, options, onChange, c }: {
  value: V; options: { value: V; label: string; count?: number }[]; onChange: (v: V) => void; c: P;
}) {
  return (
    <div role="tablist" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: 8, background: c.surface2, border: `1px solid ${c.line}` }}>
      {options.map(o => {
        const active = o.value === value;
        return (
          <button key={o.value} role="tab" aria-selected={active} onClick={() => onChange(o.value)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: 600, fontFamily: sans,
            background: active ? c.surface : 'transparent', color: active ? c.ink : c.slate,
            boxShadow: active ? (c.isDark ? '0 0 0 1px #262626' : '0 1px 2px rgba(15,23,42,.08)') : 'none',
          }}>
            {o.label}
            {o.count !== undefined && (
              <span style={{ fontSize: 11, fontWeight: 600, color: active ? c.slate : c.muted }}>{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ icon, title, body, action, c }: { icon: string; title: string; body: React.ReactNode; action?: React.ReactNode; c: P }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: c.accentBg, color: c.accent,
      }}><Icon d={I[icon]} size={20} /></div>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink }}>{title}</div>
      <div style={{ fontSize: 13, color: c.slate, marginTop: 6, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.55 }}>{body}</div>
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, footer, c, width = 480 }: {
  title: string; subtitle?: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode; c: P; width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title} style={{
        width: '100%', maxWidth: width, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, fontFamily: sans,
      }}>
        <div style={{ padding: '18px 20px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button aria-label="Close" onClick={onClose} style={{ ...btn(c, 'ghost', true), padding: 4 }}><Icon d={I.close} size={16} /></button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto' }}>{children}</div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{footer}</div>
      </div>
    </div>
  );
}

/** Slide-over below the app header; no backdrop. Clicks outside close it unless they land on an element marked data-keep-panel. */
function SidePanel({ open, onClose, width = 480, children, c, labelText }: {
  open: boolean; onClose: () => void; width?: number; children: React.ReactNode; c: P; labelText: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) { setMounted(true); return; }
    const t = window.setTimeout(() => setMounted(false), PANEL_MS);
    return () => window.clearTimeout(t);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (ref.current?.contains(target)) return;
      if (target.closest('[data-keep-panel]') || target.closest('[role="dialog"]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[aria-modal="true"]')) onClose(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);
  if (!mounted) return null;
  return (
    <div ref={ref} role="complementary" aria-label={labelText} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: `min(${width}px, 100vw)`, zIndex: 1200,
      background: c.surface, borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${open ? 'alertPanelIn' : 'alertPanelOut'} ${PANEL_MS}ms ${open ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes alertPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes alertPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
      `}</style>
      {children}
    </div>
  );
}

function TrendBars({ data, c }: { data: { date: string; count: number }[]; c: P }) {
  const days = useMemo(() => {
    const map = new Map(data.map(d => [d.date, d.count]));
    const out: { date: string; count: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      out.push({ date: key, count: map.get(key) || 0 });
    }
    return out;
  }, [data]);
  const max = Math.max(1, ...days.map(d => d.count));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 28 }} aria-hidden="true">
      {days.map(d => (
        <div key={d.date} title={`${d.date}: ${d.count} alert${d.count === 1 ? '' : 's'}`} style={{
          flex: 1, minWidth: 2, borderRadius: 2,
          height: d.count ? Math.max(4, (d.count / max) * 28) : 2,
          background: d.count ? c.accent : c.line,
        }} />
      ))}
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */
export default function Monitoring() {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const c = palette(mode === 'dark');
  const canEdit = !!user && user.role !== 'viewer';
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const [tab, setTab] = useState<Tab>('alerts');
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'error' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const notify = (text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2800);
  };

  const [ruleEditor, setRuleEditor] = useState<{ rule: Rule | null; preset?: Partial<Rule> } | null>(null);
  const [silenceDraft, setSilenceDraft] = useState<{ vendorId?: string | null; ruleId?: string | null } | null>(null);

  const statsQuery = useQuery(monitoringQueries.stats());
  const rulesQuery = useQuery(monitoringQueries.rules());
  const vendorsQuery = useQuery(monitoringQueries.vendorOverview());
  const silencesQuery = useQuery(monitoringQueries.silences());
  const channelsQuery = useQuery(monitoringQueries.channels());

  const stats = statsQuery.data as Stats | undefined;
  const rules: Rule[] = rulesQuery.data ?? [];
  const vendors: VendorRow[] = vendorsQuery.data ?? [];
  const silences: Silence[] = silencesQuery.data ?? [];
  const channels: Channel[] = channelsQuery.data ?? [];

  const enabledChannels = channels.filter(ch => ch.is_active && parseJson(ch.config).enabled);
  const activeRules = rules.filter(r => r.is_active).length;

  const tabs: { key: Tab; label: string; count?: number; alert?: boolean }[] = [
    { key: 'alerts', label: 'Alerts', count: stats?.open_alerts, alert: !!stats?.open_alerts },
    { key: 'rules', label: 'Rules', count: rules.length },
    { key: 'silences', label: 'Silences', count: silences.length },
    { key: 'vendors', label: 'Vendors', count: vendors.length },
    { key: 'channels', label: 'Delivery' },
  ];

  return (
    <div style={{ background: c.bg, minHeight: '100%', padding: '24px 28px 40px', fontFamily: sans, color: c.ink }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: c.ink, letterSpacing: '-.01em' }}>Alerting</h1>
          <div style={{ fontSize: 13, color: c.slate, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {!stats || rulesQuery.isPending || channelsQuery.isPending ? 'Loading alerting status…' : (<>
            Every completed scan is checked against {activeRules} active rule{activeRules === 1 ? '' : 's'} for {stats.vendors_watched} vendor{stats.vendors_watched === 1 ? '' : 's'}.
            {enabledChannels.length > 0
              ? <span>Alerts are sent to {enabledChannels.map(ch => ch.name).join(', ')}.</span>
              : (
                <button onClick={() => setTab('channels')} style={{ ...btn(c, 'ghost', true), padding: 0, color: c.amber, fontWeight: 600 }}>
                  <Icon d={I.warn} size={13} /> Alerts only show here — set up email or Slack delivery
                </button>
              )}
            </>)}
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn(c)} onClick={() => setSilenceDraft({})}><Icon d={I.mute} /> Silence alerts</button>
            <button style={btn(c, 'primary')} onClick={() => setRuleEditor({ rule: null })}><Icon d={I.plus} /> New rule</button>
          </div>
        )}
      </div>

      {/* Health strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 22 }}>
        <div style={{ ...panel(c), padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: c.slate }}>Unresolved alerts</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: stats?.open_alerts ? c.red : c.ink, lineHeight: 1 }}>{stats?.open_alerts ?? '—'}</span>
            {!!stats && stats.unacknowledged_alerts > 0 && (
              <span style={{ fontSize: 12, color: c.slate }}>{stats.unacknowledged_alerts} not acknowledged</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {SEVERITIES.map(s => {
              const n = stats?.open_by_severity?.[s] ?? stats?.open_by_severity?.[SEV[s].label] ?? 0;
              return n ? (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><SevChip sev={s} c={c} /><span style={{ fontSize: 12, fontWeight: 600, color: c.ink2 }}>{n}</span></span>
              ) : null;
            })}
            {!stats?.open_alerts && <span style={{ fontSize: 12, color: c.green, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon d={I.check} size={13} /> All clear</span>}
          </div>
        </div>
        <div style={{ ...panel(c), padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: c.slate }}>Alerts, last 30 days</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: c.ink }}>{(stats?.daily_trend || []).reduce((n, d) => n + d.count, 0)}</span>
          </div>
          <div style={{ marginTop: 14 }}><TrendBars data={stats?.daily_trend || []} c={c} /></div>
        </div>
        <div style={{ ...panel(c), padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: c.slate }}>Coverage</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: c.ink, marginTop: 6, lineHeight: 1 }}>
            {stats?.vendors_watched ?? '—'}<span style={{ fontSize: 13, fontWeight: 600, color: c.slate }}> vendors watched</span>
          </div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 10 }}>
            {stats?.vendors_paused ? <Chip c={c} tone="amber"><Icon d={I.pause} size={11} /> {stats.vendors_paused} paused</Chip> : 'No vendors paused'}
          </div>
        </div>
        <div style={{ ...panel(c), padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: c.slate }}>Noise control</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: c.ink, marginTop: 6, lineHeight: 1 }}>
            {activeRules}<span style={{ fontSize: 13, fontWeight: 600, color: c.slate }}> active rules</span>
          </div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 10 }}>
            {silences.length ? <Chip c={c} tone="blue"><Icon d={I.mute} size={11} /> {silences.length} silence{silences.length === 1 ? '' : 's'} running</Chip> : 'No silences running'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${c.line}`, marginBottom: 18, overflowX: 'auto', overflowY: 'hidden' }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} role="tab" aria-selected={active} onClick={() => setTab(t.key)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 14px', marginBottom: -1, cursor: 'pointer',
              background: 'none', border: 'none', borderBottom: `2px solid ${active ? c.accent : 'transparent'}`,
              color: active ? c.ink : c.slate, fontSize: 13, fontWeight: 600, fontFamily: sans, whiteSpace: 'nowrap',
            }}>
              {t.label}
              {t.count !== undefined && (
                <span style={{
                  minWidth: 18, padding: '1px 6px', borderRadius: 99, fontSize: 11, fontWeight: 700, textAlign: 'center',
                  background: t.alert ? (c.isDark ? '#7f1d1d' : '#FEE2E2') : c.surface2,
                  color: t.alert ? (c.isDark ? '#FECACA' : '#B91C1C') : c.slate,
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'alerts' && (
        <AlertsTab c={c} canEdit={canEdit} vendors={vendors} rules={rules} activeRules={activeRules}
          onSilence={(vendorId, ruleId) => setSilenceDraft({ vendorId, ruleId })} notify={notify} />
      )}
      {tab === 'rules' && (
        <RulesTab c={c} canEdit={canEdit} isAdmin={isAdmin} rules={rules} loading={rulesQuery.isPending} vendors={vendors}
          onEdit={rule => setRuleEditor({ rule })} onDuplicate={rule => setRuleEditor({ rule: null, preset: { ...rule, name: `${rule.name} (copy)` } })}
          onCreate={() => setRuleEditor({ rule: null })} notify={notify} />
      )}
      {tab === 'silences' && (
        <SilencesTab c={c} canEdit={canEdit} silences={silences} loading={silencesQuery.isPending} vendors={vendors} rules={rules}
          onCreate={() => setSilenceDraft({})} notify={notify} />
      )}
      {tab === 'vendors' && (
        <VendorsTab c={c} canEdit={canEdit} vendors={vendors} loading={vendorsQuery.isPending} notify={notify} />
      )}
      {tab === 'channels' && (
        <ChannelsTab c={c} canEdit={canEdit} channels={channels} loading={channelsQuery.isPending} notify={notify} />
      )}

      {ruleEditor && (
        <RuleEditor c={c} rule={ruleEditor.rule} preset={ruleEditor.preset} vendors={vendors}
          onClose={() => setRuleEditor(null)} notify={notify} />
      )}
      {silenceDraft && (
        <SilenceModal c={c} vendors={vendors} rules={rules} initialVendor={silenceDraft.vendorId ?? null} initialRule={silenceDraft.ruleId ?? null}
          onClose={() => setSilenceDraft(null)} notify={notify} />
      )}

      {toast && (
        <div role="status" style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, padding: '9px 16px', borderRadius: 8,
          background: toast.tone === 'error' ? '#B91C1C' : (c.isDark ? '#1e293b' : T.ink), color: '#fff', fontSize: 12.5, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8, fontFamily: sans,
        }}><Icon d={toast.tone === 'error' ? I.warn : I.check} size={13} />{toast.text}</div>
      )}
    </div>
  );
}

/* ─── Alerts (inbox) ─────────────────────────────────────────────── */
function AlertsTab({ c, canEdit, vendors, rules, activeRules, onSilence, notify }: {
  c: P; canEdit: boolean; vendors: VendorRow[]; rules: Rule[]; activeRules: number;
  onSilence: (vendorId: string | null, ruleId: string | null) => void; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const [status, setStatus] = useState<'open' | 'acknowledged' | 'resolved' | 'all'>('open');
  const [severity, setSeverity] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setQ(search.trim()), 300); return () => window.clearTimeout(t); }, [search]);
  useEffect(() => { setPage(0); setSelected(new Set()); }, [status, severity, vendorId, type, q]);

  const filters: AlertFilters = { status, severity, vendorId, type, q, offset: page * PAGE_SIZE, limit: PAGE_SIZE };
  const alertsQuery = useQuery({ ...monitoringQueries.alerts(filters), placeholderData: keepPreviousData });
  const alerts: AlertItem[] = alertsQuery.data?.alerts ?? [];
  const counts = alertsQuery.data?.counts ?? {};
  const total = alertsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const detailQuery = useQuery({ ...monitoringQueries.alert(openId || ''), enabled: !!openId });
  const openAlert: AlertItem | null = openId
    ? ((detailQuery.data as AlertItem | undefined)?.id === openId ? detailQuery.data as AlertItem : alerts.find(a => a.id === openId) || null)
    : null;
  const anyFilter = !!(severity || vendorId || type || q);

  const action = useMutation({
    mutationFn: ({ ids, action: act, note }: { ids: string[]; action: string; note?: string }) =>
      api.post('/v1/monitoring/alerts/bulk', { ids, action: act, note }),
    meta: { invalidates: INVALIDATE },
  });
  const run = (ids: string[], act: 'read' | 'acknowledge' | 'resolve' | 'reopen', note?: string) =>
    action.mutateAsync({ ids, action: act, note }).then(() => {
      const verb = { read: 'Marked as read', acknowledge: 'Acknowledged', resolve: 'Resolved', reopen: 'Reopened' }[act];
      notify(ids.length > 1 ? `${verb} ${ids.length} alerts` : verb);
      setSelected(new Set());
    }).catch(e => notify(apiError(e, 'That action failed. Try again.'), 'error'));

  const openRow = (a: AlertItem) => {
    setOpenId(a.id);
    if (!a.is_read) action.mutate({ ids: [a.id], action: 'read' });
  };

  const groups = useMemo(() => {
    const out: { day: string; items: AlertItem[] }[] = [];
    alerts.forEach(a => {
      const day = dayLabel(a.created_at);
      const last = out[out.length - 1];
      if (last && last.day === day) last.items.push(a); else out.push({ day, items: [a] });
    });
    return out;
  }, [alerts]);

  const allSelected = alerts.length > 0 && alerts.every(a => selected.has(a.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(alerts.map(a => a.id)));
  const toggleOne = (id: string) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectedAlerts = alerts.filter(a => selected.has(a.id));

  const typesInUse = Object.keys(TRIGGERS);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <Segmented c={c} value={status} onChange={setStatus} options={[
          { value: 'open', label: 'Open', count: counts.open ?? 0 },
          { value: 'acknowledged', label: 'Acknowledged', count: counts.acknowledged ?? 0 },
          { value: 'resolved', label: 'Resolved', count: counts.resolved ?? 0 },
          { value: 'all', label: 'All', count: counts.all ?? 0 },
        ]} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Icon d={I.search} size={14} style={{ position: 'absolute', left: 10, top: 10, color: c.muted }} />
            <input aria-label="Search alerts" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search message or vendor"
              style={{ ...input(c), width: 230, paddingLeft: 30 }} />
          </div>
          <select aria-label="Severity" value={severity} onChange={e => setSeverity(e.target.value)} style={{ ...input(c), width: 140 }}>
            <option value="">All severities</option>
            {SEVERITIES.map(s => <option key={s} value={s}>{SEV[s].label}</option>)}
          </select>
          <select aria-label="Vendor" value={vendorId} onChange={e => setVendorId(e.target.value)} style={{ ...input(c), width: 170 }}>
            <option value="">All vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select aria-label="Trigger" value={type} onChange={e => setType(e.target.value)} style={{ ...input(c), width: 180 }}>
            <option value="">All triggers</option>
            {typesInUse.map(t => <option key={t} value={t}>{TRIGGERS[t].label}</option>)}
          </select>
          {anyFilter && (
            <button style={btn(c, 'ghost', true)} onClick={() => { setSeverity(''); setVendorId(''); setType(''); setSearch(''); }}>Clear filters</button>
          )}
        </div>
      </div>

      <div style={{ ...panel(c), overflow: 'hidden' }}>
        {/* list header / bulk bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px', minHeight: 40, boxSizing: 'border-box',
          borderBottom: `1px solid ${c.line}`, background: selected.size ? c.accentBg : c.surface2,
        }}>
          <input type="checkbox" aria-label="Select all alerts on this page" checked={allSelected} onChange={toggleAll} disabled={!alerts.length || !canEdit} />
          {selected.size > 0 && canEdit ? (
            <>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{selected.size} selected</span>
              <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                {selectedAlerts.some(a => alertStatus(a) === 'open') && (
                  <button style={btn(c, 'secondary', true)} onClick={() => run(Array.from(selected), 'acknowledge')}>Acknowledge</button>
                )}
                {selectedAlerts.some(a => alertStatus(a) !== 'resolved') && (
                  <button style={btn(c, 'secondary', true)} onClick={() => run(Array.from(selected), 'resolve')}>Resolve</button>
                )}
                {selectedAlerts.some(a => alertStatus(a) !== 'open') && (
                  <button style={btn(c, 'secondary', true)} onClick={() => run(Array.from(selected), 'reopen')}>Reopen</button>
                )}
                <button style={btn(c, 'ghost', true)} onClick={() => setSelected(new Set())}>Cancel</button>
              </div>
            </>
          ) : (
            <span style={{ fontSize: 12, color: c.slate }}>
              {alertsQuery.isPending ? 'Loading…' : `${total} alert${total === 1 ? '' : 's'}`}
            </span>
          )}
        </div>

        {alertsQuery.isError && (
          <div style={{ padding: 20, fontSize: 13, color: c.red }}>Could not load alerts. <button style={btn(c, 'ghost', true)} onClick={() => alertsQuery.refetch()}>Retry</button></div>
        )}

        {!alertsQuery.isPending && !alertsQuery.isError && alerts.length === 0 && (
          anyFilter ? (
            <EmptyState c={c} icon="search" title="No alerts match these filters" body="Try a different severity, vendor or trigger." />
          ) : status === 'open' ? (
            <EmptyState c={c} icon="check" title="All clear"
              body={<>No open alerts. FluxWatch checks every completed scan against {activeRules} active rule{activeRules === 1 ? '' : 's'} and raises an alert here when something gets worse.</>} />
          ) : (
            <EmptyState c={c} icon="bell" title={`No ${status === 'all' ? '' : status + ' '}alerts`} body="Alerts appear here as scans complete." />
          )
        )}

        {groups.map(g => (
          <div key={g.day}>
            <div style={{ padding: '7px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: c.muted, background: c.surface2, borderBottom: `1px solid ${c.line2}` }}>{g.day}</div>
            {g.items.map(a => {
              const st = alertStatus(a);
              const active = a.id === openId;
              const vendor = vendors.find(v => v.id === a.vendor_id);
              return (
                <div key={a.id} data-keep-panel onClick={() => openRow(a)} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') openRow(a); }}
                  style={{
                    display: 'grid', gridTemplateColumns: 'auto 96px minmax(0, 1fr) 200px 110px 110px', alignItems: 'center', gap: 14,
                    padding: '12px 16px', borderBottom: `1px solid ${c.line2}`, cursor: 'pointer',
                    background: active ? c.accentBg : selected.has(a.id) ? c.hover : 'transparent',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = c.hover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = active ? c.accentBg : selected.has(a.id) ? c.hover : 'transparent'; }}>
                  <input type="checkbox" aria-label="Select alert" checked={selected.has(a.id)} disabled={!canEdit}
                    onClick={e => e.stopPropagation()} onChange={() => toggleOne(a.id)} />
                  <div><SevChip sev={a.severity} c={c} /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {!a.is_read && <span title="Unread" style={{ width: 7, height: 7, borderRadius: 99, background: c.accent, flexShrink: 0 }} />}
                      <span style={{ fontSize: 13, fontWeight: a.is_read ? 600 : 700, color: c.ink }}>{ALERT_TYPE_LABEL(a.alert_type)}</span>
                      {a.old_value && a.new_value && a.old_value.length < 12 && a.new_value.length < 12 && (
                        <span style={{ fontSize: 12, color: c.slate, fontVariantNumeric: 'tabular-nums' }}>{a.old_value} → <strong style={{ color: c.ink }}>{a.new_value}</strong></span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.message}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <VendorLogo name={a.vendor_name || '?'} domain={vendor?.domain || ''} size={22} isDark={c.isDark} />
                    <span style={{ fontSize: 12.5, color: c.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.vendor_name || 'Unknown vendor'}</span>
                  </div>
                  <div><StatusChip status={st} c={c} /></div>
                  <div style={{ fontSize: 12, color: c.slate, textAlign: 'right' }} title={fmtDateTime(a.created_at)}>{ago(a.created_at)}</div>
                </div>
              );
            })}
          </div>
        ))}

        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${c.line}` }}>
            <span style={{ fontSize: 12, color: c.slate }}>{page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={btn(c, 'secondary', true)} disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
              <button style={btn(c, 'secondary', true)} disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <SidePanel open={!!openAlert} onClose={() => setOpenId(null)} c={c} labelText="Alert details">
        {openAlert && (
          <AlertDetail key={openAlert.id} c={c} alert={openAlert} canEdit={canEdit} rules={rules} vendors={vendors}
            busy={action.isPending} onClose={() => setOpenId(null)}
            onAction={(act, note) => run([openAlert.id], act, note)}
            onSilence={() => onSilence(openAlert.vendor_id, openAlert.rule_id)} />
        )}
      </SidePanel>
    </div>
  );
}

function AlertDetail({ c, alert, canEdit, rules, vendors, busy, onClose, onAction, onSilence }: {
  c: P; alert: AlertItem; canEdit: boolean; rules: Rule[]; vendors: VendorRow[]; busy: boolean; onClose: () => void;
  onAction: (act: 'acknowledge' | 'resolve' | 'reopen', note?: string) => void; onSilence: () => void;
}) {
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const st = alertStatus(alert);
  const rule = rules.find(r => r.id === alert.rule_id);
  const vendor = vendors.find(v => v.id === alert.vendor_id);
  const details = parseJson(alert.details);

  const timeline = [
    { label: 'Alert raised', who: rule ? `by rule “${rule.name}”` : null, at: alert.created_at, tone: c.red },
    alert.acknowledged_at ? { label: 'Acknowledged', who: alert.acknowledged_by ? `by ${alert.acknowledged_by}` : null, at: alert.acknowledged_at, tone: c.amber } : null,
    alert.resolved_at ? { label: 'Resolved', who: alert.resolved_by ? `by ${alert.resolved_by}` : null, at: alert.resolved_at, tone: c.green } : null,
  ].filter(Boolean) as { label: string; who: string | null; at: string; tone: string }[];

  const section = (title: string, body: React.ReactNode) => (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8 }}>{title}</div>
      {body}
    </div>
  );

  const list = (items: string[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((x, i) => (
        <div key={i} style={{ fontSize: 12.5, color: c.ink2, padding: '7px 10px', borderRadius: 7, background: c.surface2 }}>{x}</div>
      ))}
    </div>
  );

  return (
    <>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <SevChip sev={alert.severity} c={c} /><StatusChip status={st} c={c} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.ink, marginTop: 10 }}>{ALERT_TYPE_LABEL(alert.alert_type)}</div>
          <div style={{ fontSize: 13, color: c.ink2, marginTop: 4, lineHeight: 1.5 }}>{alert.message}</div>
        </div>
        <button aria-label="Close" onClick={onClose} style={{ ...btn(c, 'ghost', true), padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <div style={{ padding: '4px 20px 20px', overflowY: 'auto', flex: 1 }}>
        {section('Vendor', (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 9, border: `1px solid ${c.line}` }}>
            <VendorLogo name={alert.vendor_name || '?'} domain={vendor?.domain || ''} size={34} isDark={c.isDark} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{alert.vendor_name || 'Unknown vendor'}</div>
              <div style={{ fontSize: 12, color: c.slate }}>
                {vendor ? <>{vendor.domain}{vendor.score !== null && <> · score {vendor.score}</>}</> : 'No longer in your vendor list'}
              </div>
            </div>
            {vendor && (
              <button style={btn(c, 'secondary', true)} onClick={() => navigate(`/vendors/${vendor.id}`)}>Open <Icon d={I.external} size={12} /></button>
            )}
          </div>
        ))}

        {(alert.old_value || alert.new_value) && alert.alert_type !== 'new_critical' && section('Change', (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 9, background: c.surface2, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: c.slate }}>Before</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.ink2, marginTop: 2 }}>{alert.old_value ?? '—'}</div>
            </div>
            <Icon d={I.chevron} size={16} style={{ color: c.muted }} />
            <div style={{ padding: 12, borderRadius: 9, background: c.surface2, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: c.slate }}>{alert.alert_type === 'cert_expiry' ? 'Days left' : 'Now'}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.red, marginTop: 2 }}>{alert.new_value ?? '—'}</div>
            </div>
          </div>
        ))}

        {Array.isArray(details.new_critical_issues) && details.new_critical_issues.length > 0 &&
          section(`New critical findings (${details.new_critical_issues.length})`, list(details.new_critical_issues))}
        {Array.isArray(details.regressed_indicators) && details.regressed_indicators.length > 0 &&
          section(`Regressed checks (${details.regressed_indicators.length})`,
            list(details.regressed_indicators.map((r: any) => `${r.name}${r.category ? ` · ${r.category}` : ''}${r.severity ? ` · ${r.severity}` : ''}`)))}
        {details.domain && section('Certificate', list([`${details.domain}`, details.expiry_date ? `Expires ${fmtDateTime(details.expiry_date)}` : '']
          .filter(Boolean)))}

        {section('Rule', rule ? (
          <div style={{ fontSize: 12.5, color: c.ink2, lineHeight: 1.5 }}>
            <strong style={{ color: c.ink }}>{rule.name}</strong> — {ruleSentence(rule, vendors)}
          </div>
        ) : <div style={{ fontSize: 12.5, color: c.slate }}>The rule that raised this alert has since been deleted.</div>)}

        {section('Timeline', (
          <div style={{ position: 'relative', paddingLeft: 18 }}>
            <div style={{ position: 'absolute', left: 4, top: 6, bottom: 6, width: 1, background: c.line }} />
            {timeline.map((t, i) => (
              <div key={i} style={{ position: 'relative', paddingBottom: i === timeline.length - 1 ? 0 : 14 }}>
                <span style={{ position: 'absolute', left: -18, top: 4, width: 9, height: 9, borderRadius: 99, background: t.tone, border: `2px solid ${c.surface}` }} />
                <div style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{t.label} <span style={{ fontWeight: 400, color: c.slate }}>{t.who}</span></div>
                <div style={{ fontSize: 11.5, color: c.muted }}>{fmtDateTime(t.at)}</div>
              </div>
            ))}
          </div>
        ))}
        {alert.resolution_note && section('Resolution note', (
          <div style={{ fontSize: 12.5, color: c.ink2, padding: 10, borderRadius: 8, background: c.surface2, whiteSpace: 'pre-wrap' }}>{alert.resolution_note}</div>
        ))}

        {resolving && (
          <div style={{ marginTop: 20 }}>
            <label style={label(c)} htmlFor="resolve-note">Resolution note <span style={{ fontWeight: 400, color: c.muted }}>(optional)</span></label>
            <textarea id="resolve-note" autoFocus value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={1000}
              placeholder="What was done, or why this needs no action" style={{ ...input(c), resize: 'vertical' }} />
          </div>
        )}
      </div>

      {canEdit && (
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {resolving ? (
            <>
              <button style={btn(c, 'primary')} disabled={busy} onClick={() => { onAction('resolve', note.trim() || undefined); setResolving(false); }}>Resolve alert</button>
              <button style={btn(c)} onClick={() => setResolving(false)}>Cancel</button>
            </>
          ) : (
            <>
              {st === 'open' && <button style={btn(c, 'primary')} disabled={busy} onClick={() => onAction('acknowledge')}>Acknowledge</button>}
              {st !== 'resolved' && <button style={btn(c, st === 'open' ? 'secondary' : 'primary')} disabled={busy} onClick={() => setResolving(true)}>Resolve…</button>}
              {st !== 'open' && <button style={btn(c)} disabled={busy} onClick={() => onAction('reopen')}>Reopen</button>}
              <button style={{ ...btn(c, 'ghost'), marginLeft: 'auto' }} onClick={onSilence}><Icon d={I.mute} /> Silence</button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ─── Rules ──────────────────────────────────────────────────────── */
const vendorNameOf = (vendors: VendorRow[], id: string | null) =>
  id ? (vendors.find(v => v.id === id)?.name || 'a vendor no longer in your list') : null;

function ruleSentence(rule: Pick<Rule, 'type' | 'vendor_id' | 'threshold_value' | 'consecutive_count'>, vendors: VendorRow[]) {
  const who = rule.vendor_id ? vendorNameOf(vendors, rule.vendor_id) : 'any vendor';
  const whose = rule.vendor_id ? `${vendorNameOf(vendors, rule.vendor_id)}'s` : "a vendor's";
  switch (rule.type) {
    case 'score_drop': return `when ${whose} score drops by ${rule.threshold_value || 10}+ points between scans.`;
    case 'score_threshold': return `when ${whose} score falls below its floor.`;
    case 'risk_change': return `when ${whose} risk level rises by its configured step.`;
    case 'new_critical': return `when a new Critical finding appears for ${who}.`;
    case 'indicator_regression': {
      const n = rule.consecutive_count || 1;
      return `when a High/Critical check that passed now fails for ${who}${n > 1 ? `, ${n} scans in a row` : ''}.`;
    }
    case 'cert_expiry': return `when ${whose} TLS certificate expires within 30 days.`;
    case 'compliance_drop': return `when ${whose} compliance score drops.`;
    default: return `for ${who}.`;
  }
}

function RulesTab({ c, canEdit, isAdmin, rules, loading, vendors, onEdit, onDuplicate, onCreate, notify }: {
  c: P; canEdit: boolean; isAdmin: boolean; rules: Rule[]; loading: boolean; vendors: VendorRow[];
  onEdit: (r: Rule) => void; onDuplicate: (r: Rule) => void; onCreate: () => void; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.put(`/v1/monitoring/alerts/rules/${id}`, { is_active: active ? 1 : 0 }),
    meta: { invalidates: INVALIDATE },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/monitoring/alerts/rules/${id}`),
    meta: { invalidates: INVALIDATE },
  });

  const sorted = [...rules].sort((a, b) => (b.is_active - a.is_active) || a.name.localeCompare(b.name));

  if (loading) return <div style={{ ...panel(c), padding: 20, fontSize: 13, color: c.slate }}>Loading rules…</div>;

  return (
    <div>
      <div style={{ fontSize: 12.5, color: c.slate, marginBottom: 12 }}>
        Rules decide what counts as an alert. They run after every completed scan, and certificate expiry is checked daily.
      </div>
      <div style={{ ...panel(c), overflow: 'hidden' }}>
        {sorted.length === 0 && (
          <EmptyState c={c} icon="bell" title="No rules yet" body="Without rules, scans still run but nothing raises an alert."
            action={canEdit ? <button style={btn(c, 'primary')} onClick={onCreate}><Icon d={I.plus} /> New rule</button> : undefined} />
        )}
        {sorted.map((r, i) => {
          const t = triggerOf(r.type);
          const scope = r.vendor_id ? vendorNameOf(vendors, r.vendor_id) : 'All vendors';
          return (
            <div key={r.id} style={{
              display: 'grid', gridTemplateColumns: 'auto 36px minmax(0, 1fr) auto', alignItems: 'center', gap: 14,
              padding: '14px 16px', borderTop: i ? `1px solid ${c.line2}` : 'none', opacity: r.is_active ? 1 : 0.6,
            }}>
              <Switch c={c} on={!!r.is_active} disabled={!canEdit || toggle.isPending} labelText={`${r.is_active ? 'Turn off' : 'Turn on'} ${r.name}`}
                onChange={v => toggle.mutateAsync({ id: r.id, active: v }).then(() => notify(v ? 'Rule turned on' : 'Rule turned off'))
                  .catch(e => notify(apiError(e, 'Could not update this rule.'), 'error'))} />
              <div style={{ width: 36, height: 36, borderRadius: 9, background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon d={I[t.icon] || I.bell} size={17} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{r.name}</span>
                  {!t.available && <Chip c={c} tone="amber" title={t.unavailableNote}><Icon d={I.warn} size={11} /> Can't fire yet</Chip>}
                </div>
                <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3 }}>
                  Raise {t.severityMode === 'rule' ? `a ${SEV[sevKey(r.severity)].label}` : 'an'} alert {ruleSentence(r, vendors)}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip c={c} tone={r.vendor_id ? 'blue' : 'neutral'}>{scope}</Chip>
                  {t.severityMode === 'rule' ? <SevChip sev={r.severity} c={c} /> : <Chip c={c} title={t.severityNote}>Severity by impact</Chip>}
                  <Chip c={c}><Icon d={I.clock} size={11} /> {cooldownLabel(r.cooldown_minutes)}</Chip>
                  <span style={{ fontSize: 12, color: c.muted, marginLeft: 4 }}>
                    {r.alerts_30d ? `${r.alerts_30d} alert${r.alerts_30d === 1 ? '' : 's'} in 30 days` : 'No alerts in 30 days'} · last fired {ago(r.last_triggered).toLowerCase()}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {canEdit && <button title="Edit" aria-label={`Edit ${r.name}`} style={btn(c, 'ghost', true)} onClick={() => onEdit(r)}><Icon d={I.edit} size={14} /></button>}
                {canEdit && t.available && <button title="Duplicate" aria-label={`Duplicate ${r.name}`} style={btn(c, 'ghost', true)} onClick={() => onDuplicate(r)}><Icon d={I.copy} size={14} /></button>}
                {isAdmin && <button title="Delete" aria-label={`Delete ${r.name}`} style={{ ...btn(c, 'ghost', true), color: c.red }} onClick={() => setDeleting(r)}><Icon d={I.trash} size={14} /></button>}
              </div>
            </div>
          );
        })}
      </div>

      {deleting && (
        <Modal c={c} title="Delete rule?" onClose={() => setDeleting(null)} width={420}
          footer={<>
            <button style={btn(c)} onClick={() => setDeleting(null)}>Cancel</button>
            <button style={btn(c, 'danger')} disabled={remove.isPending} onClick={() => remove.mutateAsync(deleting.id)
              .then(() => { setDeleting(null); notify('Rule deleted'); })
              .catch(e => notify(apiError(e, 'Could not delete this rule.'), 'error'))}>{remove.isPending ? 'Deleting…' : 'Delete rule'}</button>
          </>}>
          <div style={{ fontSize: 13, color: c.ink2, lineHeight: 1.55 }}>
            <strong>{deleting.name}</strong> will stop raising alerts. Alerts it already raised stay in the inbox. To stop it temporarily, turn it off instead.
          </div>
        </Modal>
      )}
    </div>
  );
}

function RuleEditor({ c, rule, preset, vendors, onClose, notify }: {
  c: P; rule: Rule | null; preset?: Partial<Rule>; vendors: VendorRow[]; onClose: () => void; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const src = rule || preset || {};
  const isEdit = !!rule;
  const [type, setType] = useState<string>(src.type || 'score_drop');
  const [name, setName] = useState(src.name || '');
  const [nameTouched, setNameTouched] = useState(!!src.name);
  const [scope, setScope] = useState<'all' | 'one'>(src.vendor_id ? 'one' : 'all');
  const [vendorId, setVendorId] = useState(src.vendor_id || '');
  const [points, setPoints] = useState(String(src.threshold_value ?? 10));
  const [consecutive, setConsecutive] = useState(String(src.consecutive_count ?? 1));
  const [severity, setSeverity] = useState<Severity>(sevKey(src.severity || 'high'));
  const [cooldown, setCooldown] = useState<number>(src.cooldown_minutes ?? 1440);
  const [error, setError] = useState('');
  const t = triggerOf(type);

  useEffect(() => { if (!nameTouched) setName(t.label); }, [type, nameTouched, t.label]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => isEdit
      ? api.put(`/v1/monitoring/alerts/rules/${rule!.id}`, body)
      : api.post('/v1/monitoring/alerts/rules', body),
    meta: { invalidates: INVALIDATE },
  });

  const submit = () => {
    setError('');
    const pts = Number(points);
    if (!name.trim()) return setError('Give the rule a name.');
    if (type === 'score_drop' && (!Number.isInteger(pts) || pts < 1 || pts > 100)) return setError('Points must be a whole number from 1 to 100.');
    if (scope === 'one' && !vendorId) return setError('Pick the vendor this rule applies to.');
    const cons = Math.max(1, Math.min(10, Number(consecutive) || 1));
    const body: Record<string, unknown> = {
      name: name.trim(),
      threshold_value: type === 'score_drop' ? pts : null,
      consecutive_count: type === 'indicator_regression' ? cons : 1,
      cooldown_minutes: cooldown,
      severity: t.severityMode === 'rule' ? severity : 'high',
    };
    if (!isEdit) { body.type = type; body.vendor_id = scope === 'one' ? vendorId : null; }
    save.mutateAsync(body).then(() => { notify(isEdit ? 'Rule saved' : 'Rule created'); onClose(); })
      .catch(e => setError(apiError(e, 'Could not save this rule.')));
  };

  const preview = `Raise ${t.severityMode === 'rule' ? `a ${SEV[severity].label}` : 'an'} alert ${ruleSentence(
    { type, vendor_id: scope === 'one' && vendorId ? vendorId : null, threshold_value: Number(points) || 10, consecutive_count: Number(consecutive) || 1 }, vendors)}`;

  const step = (n: number, title: string, body: React.ReactNode, hint?: string) => (
    <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 12, marginBottom: 22 }}>
      <div style={{ width: 24, height: 24, borderRadius: 99, background: c.surface2, border: `1px solid ${c.line}`, fontSize: 12, fontWeight: 700, color: c.slate, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, marginTop: 2 }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: c.slate, marginTop: 2 }}>{hint}</div>}
        <div style={{ marginTop: 10 }}>{body}</div>
      </div>
    </div>
  );

  return (
    <SidePanel open onClose={onClose} c={c} width={560} labelText={isEdit ? 'Edit rule' : 'New rule'}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{isEdit ? 'Edit rule' : 'New alert rule'}</div>
          <div style={{ fontSize: 12.5, color: c.slate, marginTop: 2 }}>Checked after every completed scan.</div>
        </div>
        <button aria-label="Close" onClick={onClose} style={{ ...btn(c, 'ghost', true), padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
        {step(1, 'What should trigger it?', (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
            {Object.entries(TRIGGERS).filter(([k, v]) => v.available || k === type).map(([k, v]) => {
              const active = k === type;
              return (
                <button key={k} type="button" disabled={isEdit} onClick={() => setType(k)} style={{
                  textAlign: 'left', padding: 12, borderRadius: 9, cursor: isEdit ? 'default' : 'pointer', fontFamily: sans,
                  border: `1.5px solid ${active ? c.accent : c.line}`, background: active ? c.accentBg : c.surface,
                  opacity: isEdit && !active ? 0.45 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: active ? c.accent : c.ink, fontSize: 12.5, fontWeight: 700 }}>
                    <Icon d={I[v.icon]} size={14} /> {v.label}
                  </div>
                  <div style={{ fontSize: 11.5, color: c.slate, marginTop: 5, lineHeight: 1.45 }}>{v.desc}</div>
                </button>
              );
            })}
          </div>
        ), isEdit ? "The trigger can't be changed on an existing rule — create a new one instead." : undefined)}

        {step(2, 'Condition', (
          <>
            {type === 'score_drop' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: c.ink2, flexWrap: 'wrap' }}>
                Score drops by at least
                <input aria-label="Points" type="number" min={1} max={100} value={points} onChange={e => setPoints(e.target.value)} style={{ ...input(c), width: 80 }} />
                points between two scans
              </div>
            )}
            {type === 'indicator_regression' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: c.ink2, flexWrap: 'wrap' }}>
                Alert after the check fails
                <select aria-label="Scans in a row" value={consecutive} onChange={e => setConsecutive(e.target.value)} style={{ ...input(c), width: 90 }}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                scan{consecutive === '1' ? '' : 's'} in a row
              </div>
            )}
            {['score_threshold', 'risk_change', 'compliance_drop'].includes(type) && (
              <div style={{ display: 'flex', gap: 8, fontSize: 12.5, color: c.ink2, padding: 10, borderRadius: 8, background: c.surface2, lineHeight: 1.5 }}>
                <Icon d={I.info} size={14} style={{ marginTop: 2, color: c.accent }} />
                <span>The threshold is set per vendor on the <strong>Vendors</strong> tab, so one rule can use a stricter floor for critical vendors.</span>
              </div>
            )}
            {['new_critical', 'cert_expiry'].includes(type) && (
              <div style={{ fontSize: 12.5, color: c.slate }}>No settings — {t.desc.charAt(0).toLowerCase() + t.desc.slice(1)}</div>
            )}
          </>
        ))}

        {step(3, 'Which vendors?', isEdit ? (
          <div style={{ fontSize: 13, color: c.ink2 }}>{src.vendor_id ? vendorNameOf(vendors, src.vendor_id) : 'All vendors'} <span style={{ color: c.muted }}>(fixed for an existing rule)</span></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Segmented c={c} value={scope} onChange={setScope} options={[{ value: 'all', label: 'All vendors' }, { value: 'one', label: 'One vendor' }]} />
            {scope === 'one' && (
              <select aria-label="Vendor" value={vendorId} onChange={e => setVendorId(e.target.value)} style={input(c)}>
                <option value="">Choose a vendor…</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.domain})</option>)}
              </select>
            )}
          </div>
        ))}

        {step(4, 'Severity', t.severityMode === 'rule' ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SEVERITIES.map(s => {
              const active = s === severity;
              const sv = SEV[s];
              return (
                <button key={s} type="button" onClick={() => setSeverity(s)} style={{
                  padding: '6px 12px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
                  border: `1.5px solid ${active ? (c.isDark ? sv.dfg : sv.fg) : c.line}`,
                  background: active ? (c.isDark ? sv.dbg : sv.bg) : c.surface, color: active ? (c.isDark ? sv.dfg : sv.fg) : c.slate,
                }}>{sv.label}</button>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: c.slate }}>Set automatically by impact. {t.severityNote}</div>
        ))}

        {step(5, 'Noise control', (
          <select aria-label="Repeat limit" value={cooldown} onChange={e => setCooldown(Number(e.target.value))} style={{ ...input(c), maxWidth: 280 }}>
            {COOLDOWNS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ), 'How often this rule may alert for the same vendor.')}

        {step(6, 'Name', (
          <input aria-label="Rule name" value={name} maxLength={120} onChange={e => { setName(e.target.value); setNameTouched(true); }} style={input(c)} />
        ))}

        <div style={{ padding: 14, borderRadius: 10, border: `1px dashed ${c.line}`, background: c.surface2 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, letterSpacing: '.05em', textTransform: 'uppercase' }}>Preview</div>
          <div style={{ fontSize: 13, color: c.ink, marginTop: 6, lineHeight: 1.5 }}>
            {preview} {cooldown ? `${cooldownLabel(cooldown)} per vendor.` : 'Every time it happens.'}
          </div>
        </div>
        {error && <div role="alert" style={{ fontSize: 12.5, color: c.red, marginTop: 12 }}>{error}</div>}
      </div>

      <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btn(c)} onClick={onClose}>Cancel</button>
        <button style={btn(c, 'primary')} disabled={save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : isEdit ? 'Save rule' : 'Create rule'}</button>
      </div>
    </SidePanel>
  );
}

/* ─── Silences ───────────────────────────────────────────────────── */
function SilencesTab({ c, canEdit, silences, loading, vendors, rules, onCreate, notify }: {
  c: P; canEdit: boolean; silences: Silence[]; loading: boolean; vendors: VendorRow[]; rules: Rule[];
  onCreate: () => void; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const end = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/monitoring/silences/${id}`),
    meta: { invalidates: INVALIDATE },
  });

  if (loading) return <div style={{ ...panel(c), padding: 20, fontSize: 13, color: c.slate }}>Loading silences…</div>;

  return (
    <div>
      <div style={{ fontSize: 12.5, color: c.slate, marginBottom: 12 }}>
        A silence stops matching alerts from being raised for a while — useful during a vendor's maintenance window or while a known issue is being fixed.
      </div>
      <div style={{ ...panel(c), overflow: 'hidden' }}>
        {silences.length === 0 && (
          <EmptyState c={c} icon="mute" title="No silences running" body="Every rule is raising alerts as normal."
            action={canEdit ? <button style={btn(c, 'primary')} onClick={onCreate}><Icon d={I.mute} /> Silence alerts</button> : undefined} />
        )}
        {silences.map((s, i) => {
          const vendor = vendors.find(v => v.id === s.vendor_id);
          const rule = rules.find(r => r.id === s.rule_id);
          const start = toDate(s.starts_at || s.created_at).getTime();
          const finish = toDate(s.ends_at).getTime();
          const pct = Math.min(100, Math.max(0, ((Date.now() - start) / Math.max(1, finish - start)) * 100));
          return (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px auto', gap: 16, alignItems: 'center', padding: '14px 16px', borderTop: i ? `1px solid ${c.line2}` : 'none' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {vendor ? <VendorLogo name={vendor.name} domain={vendor.domain} size={22} isDark={c.isDark} /> : null}
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{s.vendor_id ? (vendor?.name || 'Removed vendor') : 'All vendors'}</span>
                  <span style={{ color: c.muted }}>·</span>
                  <span style={{ fontSize: 13, color: c.ink2 }}>{s.rule_id ? (rule?.name || 'Deleted rule') : 'All rules'}</span>
                </div>
                <div style={{ fontSize: 12.5, color: c.slate, marginTop: 4 }}>
                  {s.reason || 'No reason given'}{s.created_by ? ` — ${s.created_by}` : ''}
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: c.slate, marginBottom: 5 }}>
                  <span style={{ fontWeight: 600, color: c.ink2 }}>{until(s.ends_at)}</span><span>until {fmtDateTime(s.ends_at)}</span>
                </div>
                <div style={{ height: 5, borderRadius: 99, background: c.surface2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: c.accent }} />
                </div>
              </div>
              {canEdit ? (
                <button style={btn(c, 'secondary', true)} disabled={end.isPending}
                  onClick={() => end.mutateAsync(s.id).then(() => notify('Silence ended')).catch(e => notify(apiError(e, 'Could not end this silence.'), 'error'))}>End now</button>
              ) : <span />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SilenceModal({ c, vendors, rules, initialVendor, initialRule, onClose, notify }: {
  c: P; vendors: VendorRow[]; rules: Rule[]; initialVendor: string | null; initialRule: string | null;
  onClose: () => void; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const [vendorId, setVendorId] = useState(initialVendor || '');
  const [ruleId, setRuleId] = useState(initialRule || '');
  const [hours, setHours] = useState<number | 'custom'>(24);
  const [custom, setCustom] = useState(() => {
    const d = new Date(Date.now() + 2 * 86400000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/monitoring/silences', body),
    meta: { invalidates: INVALIDATE },
  });

  const endsAt = hours === 'custom' ? new Date(custom) : new Date(Date.now() + hours * 3600000);
  const vendorName = vendorId ? vendors.find(v => v.id === vendorId)?.name : null;
  const ruleName = ruleId ? rules.find(r => r.id === ruleId)?.name : null;

  const submit = () => {
    setError('');
    if (isNaN(endsAt.getTime()) || endsAt.getTime() <= Date.now()) return setError('Pick an end time in the future.');
    if (endsAt.getTime() - Date.now() > 90 * 86400000) return setError('A silence can last at most 90 days.');
    // Send local wall-clock time without an offset: the backend compares against its own local CURRENT_TIMESTAMP.
    const pad = (n: number) => String(n).padStart(2, '0');
    const local = `${endsAt.getFullYear()}-${pad(endsAt.getMonth() + 1)}-${pad(endsAt.getDate())}T${pad(endsAt.getHours())}:${pad(endsAt.getMinutes())}:00`;
    create.mutateAsync({ vendor_id: vendorId || null, rule_id: ruleId || null, reason: reason.trim() || null, ends_at: local })
      .then(() => { notify('Silence started'); onClose(); })
      .catch(e => setError(apiError(e, 'Could not start this silence.')));
  };

  return (
    <Modal c={c} title="Silence alerts" subtitle="Matching alerts won't be raised until the silence ends." onClose={onClose} width={500}
      footer={<>
        <button style={btn(c)} onClick={onClose}>Cancel</button>
        <button style={btn(c, 'primary')} disabled={create.isPending} onClick={submit}>{create.isPending ? 'Starting…' : 'Start silence'}</button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={label(c)} htmlFor="sil-vendor">Vendor</label>
            <select id="sil-vendor" value={vendorId} onChange={e => setVendorId(e.target.value)} style={input(c)}>
              <option value="">All vendors</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label style={label(c)} htmlFor="sil-rule">Rule</label>
            <select id="sil-rule" value={ruleId} onChange={e => setRuleId(e.target.value)} style={input(c)}>
              <option value="">All rules</option>
              {rules.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <span style={label(c)}>For how long?</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SILENCE_DURATIONS.map(d => (
              <button key={d.hours} type="button" onClick={() => setHours(d.hours)} style={{
                ...btn(c, 'secondary', true), borderColor: hours === d.hours ? c.accent : c.line,
                background: hours === d.hours ? c.accentBg : c.surface, color: hours === d.hours ? c.accent : c.ink,
              }}>{d.label}</button>
            ))}
            <button type="button" onClick={() => setHours('custom')} style={{
              ...btn(c, 'secondary', true), borderColor: hours === 'custom' ? c.accent : c.line,
              background: hours === 'custom' ? c.accentBg : c.surface, color: hours === 'custom' ? c.accent : c.ink,
            }}>Until…</button>
          </div>
          {hours === 'custom' && (
            <input aria-label="Silence until" type="datetime-local" value={custom} onChange={e => setCustom(e.target.value)} style={{ ...input(c), marginTop: 8, maxWidth: 260 }} />
          )}
        </div>
        <div>
          <label style={label(c)} htmlFor="sil-reason">Reason</label>
          <input id="sil-reason" value={reason} maxLength={300} onChange={e => setReason(e.target.value)} placeholder="e.g. Vendor's planned certificate rotation" style={input(c)} />
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 12, borderRadius: 9, background: !vendorId && !ruleId ? (c.isDark ? '#241f08' : '#FEFCE8') : c.surface2, fontSize: 12.5, color: c.ink2, lineHeight: 1.5 }}>
          <Icon d={!vendorId && !ruleId ? I.warn : I.info} size={14} style={{ marginTop: 2, color: !vendorId && !ruleId ? c.amber : c.accent }} />
          <span>
            {!vendorId && !ruleId
              ? <>This mutes <strong>every alert for every vendor</strong> until {isNaN(endsAt.getTime()) ? '…' : fmtDateTime(endsAt.toISOString())}.</>
              : <>No <strong>{ruleName || 'alerts'}</strong>{ruleName ? ' alerts' : ''} for <strong>{vendorName || 'any vendor'}</strong> until {isNaN(endsAt.getTime()) ? '…' : fmtDateTime(endsAt.toISOString())}.</>}
          </span>
        </div>
        {error && <div role="alert" style={{ fontSize: 12.5, color: c.red }}>{error}</div>}
      </div>
    </Modal>
  );
}

/* ─── Vendors (per-vendor sensitivity + pause) ───────────────────── */
function VendorsTab({ c, canEdit, vendors, loading, notify }: {
  c: P; canEdit: boolean; vendors: VendorRow[]; loading: boolean; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);

  const pause = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => api.patch(`/v1/vendors/${id}/monitoring-status`, { paused }),
    meta: { invalidates: [...INVALIDATE, ['vendors']] },
  });
  const drift = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, number> }) => api.put(`/v1/monitoring/drift/${id}`, body),
    meta: { invalidates: INVALIDATE },
  });
  const saveDrift = (v: VendorRow, body: Record<string, number>) =>
    drift.mutateAsync({ id: v.id, body }).then(() => notify(`Saved for ${v.name}`)).catch(e => notify(apiError(e, 'Could not save.'), 'error'));

  const shown = vendors.filter(v =>
    (!search || `${v.name} ${v.domain}`.toLowerCase().includes(search.toLowerCase())) && (!onlyOpen || v.open_alerts > 0));

  if (loading) return <div style={{ ...panel(c), padding: 20, fontSize: 13, color: c.slate }}>Loading vendors…</div>;

  const headCell: React.CSSProperties = { padding: '9px 14px', fontSize: 11, fontWeight: 700, color: c.muted, letterSpacing: '.04em', textTransform: 'uppercase', textAlign: 'left', background: c.surface2, borderBottom: `1px solid ${c.line}` };
  const cell: React.CSSProperties = { padding: '11px 14px', fontSize: 13, color: c.ink, borderBottom: `1px solid ${c.line2}`, verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: c.slate, maxWidth: 640 }}>
          Thresholds per vendor for the <strong>Score below floor</strong> and <strong>Risk level increased</strong> rules. Pausing a vendor stops all of its alerts; its scans keep running.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: c.slate }}>
            <input type="checkbox" checked={onlyOpen} onChange={e => setOnlyOpen(e.target.checked)} /> With open alerts
          </label>
          <div style={{ position: 'relative' }}>
            <Icon d={I.search} size={14} style={{ position: 'absolute', left: 10, top: 10, color: c.muted }} />
            <input aria-label="Search vendors" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors" style={{ ...input(c), width: 220, paddingLeft: 30 }} />
          </div>
        </div>
      </div>
      <div style={{ ...panel(c), overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              <th style={headCell}>Vendor</th>
              <th style={headCell}>Score</th>
              <th style={headCell}>Open alerts</th>
              <th style={headCell} title="Score below floor rule fires under this score">Score floor</th>
              <th style={headCell} title="Risk level increased rule fires on a rise of this many levels">Risk rise</th>
              <th style={headCell}>Last alert</th>
              <th style={{ ...headCell, textAlign: 'right' }}>Alerts</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={7} style={{ ...cell, textAlign: 'center', color: c.slate, padding: 28 }}>{vendors.length ? 'No vendors match.' : 'No vendors yet.'}</td></tr>
            )}
            {shown.map(v => (
              <tr key={v.id} style={{ opacity: v.monitoring_paused ? 0.62 : 1 }}>
                <td style={cell}>
                  <button onClick={() => navigate(`/vendors/${v.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: sans }}>
                    <VendorLogo name={v.name} domain={v.domain} size={28} isDark={c.isDark} />
                    <span>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: c.ink }}>{v.name}</span>
                      <span style={{ display: 'block', fontSize: 12, color: c.slate }}>{v.domain}</span>
                    </span>
                  </button>
                </td>
                <td style={cell}>
                  {v.score !== null ? (
                    <span style={{ fontWeight: 700, color: v.score < v.score_threshold ? c.red : c.ink }}>{v.score}<span style={{ fontWeight: 500, color: c.muted }}>/100</span></span>
                  ) : <span style={{ color: c.muted }}>Not scanned</span>}
                </td>
                <td style={cell}>
                  {v.open_alerts ? (
                    <Chip c={c} tone={v.critical_open ? 'red' : 'amber'}>{v.open_alerts} open{v.critical_open ? ` · ${v.critical_open} critical` : ''}</Chip>
                  ) : <span style={{ fontSize: 12.5, color: c.muted }}>None</span>}
                </td>
                <td style={cell}>
                  <NumberCell c={c} value={v.score_threshold} min={1} max={100} disabled={!canEdit} label={`Score floor for ${v.name}`}
                    onCommit={n => saveDrift(v, { score_threshold: n })} />
                </td>
                <td style={cell}>
                  <select aria-label={`Risk rise for ${v.name}`} value={v.risk_level_drift} disabled={!canEdit}
                    onChange={e => saveDrift(v, { risk_level_drift: Number(e.target.value) })} style={{ ...input(c), width: 120, padding: '6px 8px' }}>
                    <option value={1}>1 level</option>
                    <option value={2}>2 levels</option>
                    <option value={3}>3 levels</option>
                  </select>
                </td>
                <td style={{ ...cell, fontSize: 12.5, color: c.slate }}>{v.last_alert_at ? ago(v.last_alert_at) : '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: v.monitoring_paused ? c.amber : c.green }}>{v.monitoring_paused ? 'Paused' : 'On'}</span>
                    <Switch c={c} on={!v.monitoring_paused} disabled={!canEdit || pause.isPending} labelText={`${v.monitoring_paused ? 'Resume' : 'Pause'} alerts for ${v.name}`}
                      onChange={on => pause.mutateAsync({ id: v.id, paused: !on }).then(() => notify(on ? `Alerts resumed for ${v.name}` : `Alerts paused for ${v.name}`))
                        .catch(e => notify(apiError(e, 'Could not update this vendor.'), 'error'))} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NumberCell({ c, value, min, max, disabled, label: aria, onCommit }: {
  c: P; value: number; min: number; max: number; disabled: boolean; label: string; onCommit: (n: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const commit = () => {
    const n = Number(draft);
    if (!Number.isInteger(n) || n < min || n > max) { setDraft(String(value)); return; }
    if (n !== value) onCommit(n);
  };
  return (
    <input aria-label={aria} type="number" min={min} max={max} value={draft} disabled={disabled}
      onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={{ ...input(c), width: 76, padding: '6px 8px' }} />
  );
}

/* ─── Delivery channels ──────────────────────────────────────────── */
const CHANNEL_META: Record<string, { label: string; icon: string; desc: string; urlLabel?: string; urlHint?: string }> = {
  email: { label: 'Email', icon: 'mail', desc: 'Send each alert to a list of addresses.' },
  slack: { label: 'Slack', icon: 'hash', desc: 'Post alerts to a Slack channel through an incoming webhook.', urlLabel: 'Incoming webhook URL', urlHint: 'https://hooks.slack.com/services/…' },
  teams: { label: 'Microsoft Teams', icon: 'teams', desc: 'Post alerts to a Teams channel through an incoming webhook.', urlLabel: 'Incoming webhook URL', urlHint: 'https://…webhook.office.com/…' },
  webhook: { label: 'Webhook', icon: 'webhook', desc: 'POST each alert as JSON to your own endpoint (SIEM, ticketing, automation).', urlLabel: 'Endpoint URL', urlHint: 'https://example.com/fluxwatch-alerts' },
};
const CHANNEL_ORDER = ['email', 'slack', 'teams', 'webhook'];

function ChannelsTab({ c, canEdit, channels, loading, notify }: {
  c: P; canEdit: boolean; channels: Channel[]; loading: boolean; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const create = useMutation({
    mutationFn: (type: string) => api.post('/v1/monitoring/channels', {
      name: CHANNEL_META[type].label, type, config: { enabled: false, webhook_url: '' },
    }),
    meta: { invalidates: INVALIDATE },
  });

  if (loading) return <div style={{ ...panel(c), padding: 20, fontSize: 13, color: c.slate }}>Loading delivery settings…</div>;

  const byType = new Map<string, Channel>();
  channels.forEach(ch => { if (!byType.has(ch.type)) byType.set(ch.type, ch); });

  return (
    <div>
      <div style={{ fontSize: 12.5, color: c.slate, marginBottom: 12 }}>
        Every alert appears in the inbox. Turn on a channel to also send it there. Each channel can skip lower-severity alerts.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        {CHANNEL_ORDER.map(type => {
          const ch = byType.get(type);
          const meta = CHANNEL_META[type];
          if (!ch) {
            return (
              <div key={type} style={{ ...panel(c), padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <ChannelHeader c={c} meta={meta} status="none" />
                <div style={{ fontSize: 12.5, color: c.slate }}>{meta.desc}</div>
                {canEdit && (
                  <div><button style={btn(c)} disabled={create.isPending}
                    onClick={() => create.mutateAsync(type).then(() => notify(`${meta.label} added`)).catch(e => notify(apiError(e, 'Could not add this channel.'), 'error'))}>
                    <Icon d={I.plus} /> Add {meta.label}</button></div>
                )}
              </div>
            );
          }
          return <ChannelCard key={ch.id} c={c} channel={ch} meta={meta} canEdit={canEdit} notify={notify} />;
        })}
      </div>
    </div>
  );
}

function ChannelHeader({ c, meta, status }: { c: P; meta: typeof CHANNEL_META[string]; status: 'on' | 'off' | 'none' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon d={I[meta.icon]} size={16} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: c.ink, flex: 1 }}>{meta.label}</div>
      {status === 'on' && <Chip c={c} tone="green"><Icon d={I.check} size={11} /> Sending</Chip>}
      {status === 'off' && <Chip c={c}>Off</Chip>}
      {status === 'none' && <Chip c={c}>Not set up</Chip>}
    </div>
  );
}

function ChannelCard({ c, channel, meta, canEdit, notify }: {
  c: P; channel: Channel; meta: typeof CHANNEL_META[string]; canEdit: boolean; notify: (t: string, tone?: 'ok' | 'error') => void;
}) {
  const saved = useMemo(() => parseJson(channel.config), [channel.config]);
  const [enabled, setEnabled] = useState<boolean>(!!saved.enabled);
  const [recipients, setRecipients] = useState<string[]>(Array.isArray(saved.recipients) ? saved.recipients : []);
  const [recipientDraft, setRecipientDraft] = useState('');
  const [url, setUrl] = useState<string>(saved.webhook_url || saved.url || '');
  const firstHeader = Object.entries((saved.headers || {}) as Record<string, string>)[0];
  const [headerName, setHeaderName] = useState(firstHeader?.[0] || '');
  const [headerValue, setHeaderValue] = useState(firstHeader?.[1] || '');
  const [minSeverity, setMinSeverity] = useState<string>(saved.min_severity || '');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [error, setError] = useState('');

  const save = useMutation({
    mutationFn: (config: Record<string, unknown>) => api.put(`/v1/monitoring/channels/${channel.id}`, config),
    meta: { invalidates: INVALIDATE },
  });
  const test = useMutation({ mutationFn: () => api.post(`/v1/monitoring/channels/${channel.id}/test`) });

  const buildConfig = (): Record<string, unknown> | null => {
    setError('');
    const base: Record<string, unknown> = { enabled, min_severity: minSeverity || null };
    if (channel.type === 'email') {
      const pending = recipientDraft.trim();
      const list = pending && isEmail(pending) && !recipients.includes(pending) ? [...recipients, pending] : recipients;
      if (enabled && list.length === 0) { setError('Add at least one email address.'); return null; }
      return { ...base, recipients: list };
    }
    if (enabled && !isHttps(url)) { setError('Enter a full https:// URL.'); return null; }
    if (channel.type === 'webhook') {
      const headers = headerName.trim() ? { [headerName.trim()]: headerValue } : {};
      return { ...base, url: url.trim(), headers };
    }
    return { ...base, webhook_url: url.trim() };
  };

  const doSave = () => {
    const config = buildConfig();
    if (!config) return Promise.reject();
    return save.mutateAsync(config);
  };
  const onSave = () => doSave().then(() => { setRecipientDraft(''); notify(`${meta.label} saved`); })
    .catch(e => { if (e) notify(apiError(e, 'Could not save.'), 'error'); });
  const onTest = () => {
    setResult(null);
    doSave()
      .then(() => test.mutateAsync())
      .then(() => setResult({ ok: true, text: 'Test alert sent. Check that it arrived.' }))
      .catch(e => { if (e) setResult({ ok: false, text: apiError(e, 'The test could not be sent.') }); });
  };

  const addRecipient = () => {
    const v = recipientDraft.trim().replace(/,$/, '');
    if (!v) return;
    if (!isEmail(v)) { setError(`"${v}" isn't a valid email address.`); return; }
    if (!recipients.includes(v)) setRecipients([...recipients, v]);
    setRecipientDraft(''); setError('');
  };

  const configured = channel.type === 'email' ? recipients.length > 0 : isHttps(url);

  return (
    <div style={{ ...panel(c), padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}><ChannelHeader c={c} meta={meta} status={saved.enabled ? 'on' : 'off'} /></div>
        <Switch c={c} on={enabled} disabled={!canEdit} labelText={`Send alerts to ${meta.label}`} onChange={setEnabled} />
      </div>
      <div style={{ fontSize: 12.5, color: c.slate, marginTop: -4 }}>{meta.desc}</div>

      {channel.type === 'email' ? (
        <div>
          <label style={label(c)} htmlFor={`rcp-${channel.id}`}>Recipients</label>
          <div style={{ ...input(c), display: 'flex', flexWrap: 'wrap', gap: 6, padding: 6, minHeight: 38 }}>
            {recipients.map(r => (
              <span key={r} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 8px', borderRadius: 6, background: c.surface2, fontSize: 12, color: c.ink2 }}>
                {r}
                {canEdit && <button aria-label={`Remove ${r}`} onClick={() => setRecipients(recipients.filter(x => x !== r))} style={{ ...btn(c, 'ghost', true), padding: 2 }}><Icon d={I.close} size={11} /></button>}
              </span>
            ))}
            <input id={`rcp-${channel.id}`} value={recipientDraft} disabled={!canEdit}
              onChange={e => setRecipientDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addRecipient(); } }}
              onBlur={addRecipient}
              placeholder={recipients.length ? 'Add another…' : 'name@company.com'}
              style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', color: c.ink, fontSize: 13, fontFamily: sans, padding: '3px 4px' }} />
          </div>
        </div>
      ) : (
        <div>
          <label style={label(c)} htmlFor={`url-${channel.id}`}>{meta.urlLabel}</label>
          <input id={`url-${channel.id}`} value={url} disabled={!canEdit} onChange={e => setUrl(e.target.value)} placeholder={meta.urlHint}
            style={{ ...input(c), fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }} />
        </div>
      )}

      {channel.type === 'webhook' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={label(c)} htmlFor={`hn-${channel.id}`}>Header <span style={{ fontWeight: 400, color: c.muted }}>(optional)</span></label>
            <input id={`hn-${channel.id}`} value={headerName} disabled={!canEdit} onChange={e => setHeaderName(e.target.value)} placeholder="Authorization" style={input(c)} />
          </div>
          <div>
            <label style={label(c)} htmlFor={`hv-${channel.id}`}>Value</label>
            <input id={`hv-${channel.id}`} type="password" value={headerValue} disabled={!canEdit} onChange={e => setHeaderValue(e.target.value)} placeholder="Bearer …" style={input(c)} autoComplete="off" />
          </div>
        </div>
      )}

      <div>
        <label style={label(c)} htmlFor={`sev-${channel.id}`}>Send</label>
        <select id={`sev-${channel.id}`} value={minSeverity} disabled={!canEdit} onChange={e => setMinSeverity(e.target.value)} style={{ ...input(c), maxWidth: 260 }}>
          <option value="">All alerts</option>
          <option value="medium">Medium and above</option>
          <option value="high">High and Critical</option>
          <option value="critical">Critical only</option>
        </select>
      </div>

      {error && <div role="alert" style={{ fontSize: 12.5, color: c.red }}>{error}</div>}
      {result && (
        <div role="status" style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: result.ok ? c.green : c.red }}>
          <Icon d={result.ok ? I.check : I.warn} size={13} /> {result.text}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 4 }}>
          <button style={btn(c, 'primary')} disabled={save.isPending} onClick={onSave}>{save.isPending && !test.isPending ? 'Saving…' : 'Save'}</button>
          <button style={btn(c)} disabled={!configured || save.isPending || test.isPending} onClick={onTest}
            title={configured ? 'Saves, then sends a sample alert' : 'Add a destination first'}>
            <Icon d={I.send} size={13} /> {test.isPending ? 'Sending…' : 'Send test'}
          </button>
        </div>
      )}
    </div>
  );
}
